import moment from 'moment';

import { isConfigEnabled } from './config/isConfigEnabled';

export const TOP_TIER_REQUIRED_FIELD = 'topTierRequired';

const getColumnFieldKey = (column = {}) => column.value || column.name;

const isEmptyValue = (value) => value === null || value === undefined || value === '';

/**
 * Returns the tier control field key from behavior config.
 */
export const getTierControlField = (behaviorConfig) => (
    behaviorConfig?.tier?.controlField || TOP_TIER_REQUIRED_FIELD
);

/**
 * Parses topTierRequired into a numeric tier or null when unknown.
 */
export const normalizeTopTierRequired = (value, behaviorConfig) => {
    if (isEmptyValue(value)) {
        return null;
    }

    const normalized = String(value).trim().toLowerCase().replace(/\s+/g, ' ');
    const tierMatch = normalized.match(/^tier\s*(\d+)$/);

    if (tierMatch) {
        const tier = Number(tierMatch[1]);
        const maxTier = behaviorConfig?.tier?.maxTierLevel ?? 2;
        return tier >= 0 && tier <= maxTier ? tier : null;
    }

    return null;
};

/**
 * Detects which tier a field key belongs to, or null when not a tier field.
 */
export const getFieldTier = (fieldKey, behaviorConfig) => {
    if (!fieldKey || !isConfigEnabled(behaviorConfig?.tier?.editability?.fieldTierDetection, true)) {
        return getFieldTierFromName(fieldKey);
    }

    return getFieldTierFromName(fieldKey);
};

/**
 * Detects tier level from field name using tier{n} pattern.
 */
const getFieldTierFromName = (fieldKey) => {
    if (!fieldKey) {
        return null;
    }

    const key = String(fieldKey);

    if (/tier\s*2/i.test(key)) {
        return 2;
    }

    if (/tier\s*1/i.test(key)) {
        return 1;
    }

    if (/tier\s*0/i.test(key)) {
        return 0;
    }

    return null;
};

/**
 * Returns true when the field can be edited for the row's current topTierRequired value.
 */
export const isFieldEditableForTopTier = (fieldKey, topTierRequired, behaviorConfig) => {
    if (!isConfigEnabled(behaviorConfig?.tier?.editability)) {
        return true;
    }

    const controlField = getTierControlField(behaviorConfig);

    if (fieldKey === controlField) {
        return behaviorConfig.tier.editability.controlFieldEditable !== false;
    }

    const fieldTier = getFieldTier(fieldKey, behaviorConfig);

    if (fieldTier === null) {
        return true;
    }

    const requiredTier = normalizeTopTierRequired(topTierRequired, behaviorConfig);

    if (requiredTier === null) {
        return true;
    }

    const strategy = behaviorConfig.tier.editability.strategy || 'lteTierMatch';

    if (strategy === 'lteTierMatch') {
        return fieldTier <= requiredTier;
    }

    return fieldTier === requiredTier;
};

/**
 * Builds a user-facing message when a tier-restricted field is clicked.
 */
export const getTierRestrictionMessage = (fieldKey, topTierRequired, behaviorConfig) => {
    const fieldTier = getFieldTier(fieldKey, behaviorConfig);
    const requiredTier = normalizeTopTierRequired(topTierRequired, behaviorConfig);
    const messages = behaviorConfig?.tier?.editability?.messages || {};

    if (fieldTier === null || requiredTier === null || fieldTier <= requiredTier) {
        return '';
    }

    if (requiredTier === 0) {
        return messages.tier0BlocksHigher
            || 'You cannot modify Tier 1 and Tier 2 fields because Tier Required is Tier 0.';
    }

    if (requiredTier === 1 && fieldTier === 2) {
        return messages.tier1BlocksTier2
            || 'You cannot modify Tier 2 fields because Tier Required is Tier 1.';
    }

    return messages.generic
        || 'You cannot modify this field for the current Tier Required value.';
};

/**
 * Safely parses a date value for tier date comparisons.
 */
const parseTierDate = (value) => {
    if (isEmptyValue(value)) {
        return null;
    }

    const parsed = moment.parseZone(value);
    return parsed.isValid() ? parsed : null;
};

/**
 * Returns configured date-validation mode: 'warning' | 'error'.
 * Warning shows inline under fields and does not block save.
 */
export const getTierDateValidationMode = (behaviorConfig) => {
    const mode = behaviorConfig?.tier?.dateValidation?.mode;
    return mode === 'error' ? 'error' : 'warning';
};

/**
 * Returns true when a date order rule fails for the given row.
 */
const doesTierDateRuleFail = (row = {}, rule = {}) => {
    const { earlierField, laterField } = rule;
    const earlierValue = row[earlierField];
    const laterValue = row[laterField];

    if (isEmptyValue(earlierValue) || isEmptyValue(laterValue)) {
        return false;
    }

    const earlierDate = parseTierDate(earlierValue);
    const laterDate = parseTierDate(laterValue);

    if (!earlierDate || !laterDate) {
        return false;
    }

    return !earlierDate.isBefore(laterDate);
};

/**
 * Returns the first failing date-order message for a specific field, or null.
 * Message stays visible on both earlier and later fields until the rule passes.
 */
export const getTierDateMessageForField = (row = {}, fieldKey, behaviorConfig) => {
    if (!fieldKey || !isConfigEnabled(behaviorConfig?.tier?.dateValidation)) {
        return null;
    }

    const rules = (behaviorConfig.tier.dateValidation.rules || []).filter((rule) => isConfigEnabled(rule));

    for (let index = 0; index < rules.length; index += 1) {
        const rule = rules[index];
        if (rule.earlierField !== fieldKey && rule.laterField !== fieldKey) {
            continue;
        }

        if (doesTierDateRuleFail(row, rule)) {
            return rule.message || null;
        }
    }

    return null;
};

/**
 * Validates ordered tier review dates on a row.
 * In warning mode returns null (does not block save); UI shows inline field messages instead.
 * In error mode returns the first failing message for save blocking.
 */
export const validateTierReviewDates = (row = {}, behaviorConfig) => {
    if (!isConfigEnabled(behaviorConfig?.tier?.dateValidation)) {
        return null;
    }

    if (getTierDateValidationMode(behaviorConfig) === 'warning') {
        return null;
    }

    const rules = (behaviorConfig.tier.dateValidation.rules || []).filter((rule) => isConfigEnabled(rule));

    for (let index = 0; index < rules.length; index += 1) {
        const rule = rules[index];
        if (doesTierDateRuleFail(row, rule)) {
            return rule.message || null;
        }
    }

    return null;
};

/**
 * Returns payload entries that should be cleared when topTierRequired changes.
 */
export const clearRestrictedTierValues = (
    row = {},
    blockHeaders = [],
    topTierRequired,
    behaviorConfig,
) => {
    if (!isConfigEnabled(behaviorConfig?.tier?.clearOnChange)) {
        return {};
    }

    const strategy = behaviorConfig.tier.clearOnChange.strategy || 'clearHigherTiers';
    const requiredTier = normalizeTopTierRequired(topTierRequired, behaviorConfig);
    const clearHistoryFields = behaviorConfig.tier.clearOnChange.clearHistoryFields === true;
    const historySuffix = behaviorConfig.tier.clearOnChange.historyFieldSuffix || 'History';

    if (requiredTier === null) {
        return {};
    }

    const clearPayload = {};

    blockHeaders.forEach((column) => {
        const fieldKey = getColumnFieldKey(column);
        const fieldTier = getFieldTier(fieldKey, behaviorConfig);

        if (fieldTier === null) {
            return;
        }

        let shouldClear = false;

        if (strategy === 'clearHigherTiers') {
            shouldClear = fieldTier > requiredTier;
        } else if (strategy === 'clearLeavingTierOnly') {
            shouldClear = fieldTier === requiredTier;
        }

        if (!shouldClear) {
            return;
        }

        if (!isEmptyValue(row[fieldKey])) {
            clearPayload[fieldKey] = null;
        }

        if (clearHistoryFields) {
            const historyKey = `${fieldKey}${historySuffix}`;
            if (!isEmptyValue(row[historyKey])) {
                clearPayload[historyKey] = null;
            }
        }
    });

    if (requiredTier === 2 && strategy === 'clearHigherTiers') {
        return clearPayload;
    }

    return clearPayload;
};

/**
 * Returns true when saving the control field should trigger tier field clearing.
 */
export const shouldClearOnControlFieldSave = (fieldKey, behaviorConfig) => {
    const controlField = getTierControlField(behaviorConfig);
    const triggerOn = behaviorConfig?.tier?.clearOnChange?.triggerOn;

    return fieldKey === controlField && triggerOn?.controlFieldSave !== false;
};
